if (!customElements.get('product-floating-bar-o2')) {
  const SELECTORS = {
    floatingButton: '[data-o2-floating-submit]',
    floatingLabel: '[data-o2-floating-label]',
    mainButton: '[data-o2-main-product-submit]',
    reservationLauncher: '[data-reservation-open]',
    reservationButton: '[data-o2-floating-reservation]',
    productInfo: 'product-info',
    chatClient: 'script[src*="widget.o2.cz/assets/client.js"]',
    chatLauncher: '#widget-chat [data-component="widget-icon-button"]',
    chatLoader: 'script[src*="widget-loader.js"]',
  };
  const CHAT_CLIENT_LOAD_TIMEOUT_MS = 15000;
  const CHAT_MOUNT_TIMEOUT_MS = 10000;

  customElements.define(
    'product-floating-bar-o2',
    class ProductFloatingBarO2 extends HTMLElement {
      connectedCallback() {
        const productForm = document.getElementById(this.dataset.productFormId)?.closest('product-form');
        this.mainButton = productForm?.querySelector(SELECTORS.mainButton);
        this.variantIdInput = productForm?.querySelector('.product-variant-id');
        this.button = this.querySelector(SELECTORS.floatingButton);
        this.label = this.querySelector(SELECTORS.floatingLabel);
        if (!this.mainButton || !this.button || !this.label) return;
        this.reservationLauncher = productForm
          ?.closest(SELECTORS.productInfo)
          ?.querySelector(SELECTORS.reservationLauncher);
        this.reservationButton = this.querySelector(SELECTORS.reservationButton);
        this.openReservation = () => this.reservationLauncher?.click();
        this.reservationButton?.addEventListener('click', this.openReservation);
        if (this.reservationLauncher) {
          this.reservationObserver = new MutationObserver(() => this.syncMainButton());
          this.reservationObserver.observe(this.reservationLauncher, {
            attributes: true,
            attributeFilter: ['hidden', 'disabled'],
          });
        }
        this.variantLabelHTML = this.label.innerHTML;
        this.syncAfterVariantInputChange = () => queueMicrotask(() => this.syncMainButton());
        this.variantIdInput?.addEventListener('change', this.syncAfterVariantInputChange);

        this.mainButtonObserver = new MutationObserver(() => this.syncMainButton());
        this.mainButtonObserver.observe(this.mainButton, {
          attributes: true,
          attributeFilter: ['aria-disabled', 'aria-haspopup', 'class', 'disabled', 'type', 'data-product-watchdog-o2'],
          childList: true,
          subtree: true,
        });
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (data.sectionId !== this.dataset.sectionId) return;

          const sourceLabel = data.html.getElementById(this.id)?.querySelector(SELECTORS.floatingLabel);
          if (sourceLabel) this.variantLabelHTML = sourceLabel.innerHTML;
          this.syncMainButton();
        });
        this.syncChatPosition = this.syncChatPosition.bind(this);
        this.armChatMountTimeout = this.armChatMountTimeout.bind(this);
        this.stopChatDiscovery = this.stopChatDiscovery.bind(this);
        this.chatRootObserver = new MutationObserver(this.syncChatPosition);
        this.observeChatRoot();
        window.addEventListener('resize', this.syncChatPosition);

        this.syncMainButton();
      }

      disconnectedCallback() {
        this.mainButtonObserver?.disconnect();
        this.reservationObserver?.disconnect();
        this.reservationButton?.removeEventListener('click', this.openReservation);
        this.variantChangeUnsubscriber?.();
        this.chatRootObserver?.disconnect();
        this.chatLauncherObserver?.disconnect();
        this.variantIdInput?.removeEventListener('change', this.syncAfterVariantInputChange);
        this.stopChatDiscovery();
        window.removeEventListener('resize', this.syncChatPosition);
      }

      syncMainButton() {
        const isWatchdog = this.mainButton.hasAttribute('data-product-watchdog-o2');
        const hasReservation = Boolean(this.reservationLauncher && !this.reservationLauncher.hidden);
        this.toggleAttribute('data-reservation-available', hasReservation);
        if (this.reservationButton) {
          this.reservationButton.hidden = !hasReservation;
          this.reservationButton.disabled = this.reservationLauncher?.disabled ?? true;
        }
        this.button.toggleAttribute('data-product-watchdog-o2', isWatchdog);
        this.button.type = this.mainButton.type;
        this.button.disabled = this.mainButton.disabled;
        this.button.classList.toggle('loading', this.mainButton.classList.contains('loading'));
        this.querySelector('.loading__spinner')?.classList.toggle(
          'hidden',
          !this.mainButton.classList.contains('loading')
        );

        ['aria-disabled', 'aria-haspopup'].forEach((attribute) => {
          const value = this.mainButton.getAttribute(attribute);
          if (value === null) {
            this.button.removeAttribute(attribute);
          } else {
            this.button.setAttribute(attribute, value);
          }
        });

        const nextLabelHTML =
          this.variantIdInput?.value && !isWatchdog
            ? this.variantLabelHTML
            : this.mainButton.querySelector(':scope > span')?.innerHTML;
        if (nextLabelHTML && this.label.innerHTML !== nextLabelHTML) {
          this.label.innerHTML = nextLabelHTML;
        }
        if (hasReservation && !isWatchdog && this.dataset.cartLabel) this.label.textContent = this.dataset.cartLabel;
        this.syncCampaignPrice();
      }

      syncCampaignPrice() {
        const productSection = document.getElementById(`MainProduct-${this.dataset.sectionId}`);
        const displayedPrice = productSection?.querySelector('[data-o2-displayed-price-label]')?.dataset
          .o2DisplayedPriceLabel;
        const floatingPrice = this.querySelector('[data-o2-floating-price]');
        if (displayedPrice && floatingPrice) {
          floatingPrice.textContent = displayedPrice;
        }
      }

      observeChatRoot() {
        const chatRoot = document.getElementById('widget-chat');
        if (!chatRoot) {
          if (!document.querySelector(`${SELECTORS.chatLoader}, ${SELECTORS.chatClient}`)) return;
          this.observeChatClient();
          if (this.chatDocumentObserver) return;

          this.chatDocumentObserver = new MutationObserver(() => this.observeChatRoot());
          this.chatDocumentObserver.observe(document.body, { childList: true, subtree: true });
          return;
        }

        this.stopChatDiscovery();
        this.chatRootObserver.observe(chatRoot, {
          attributes: true,
          attributeFilter: ['style'],
          childList: true,
          subtree: true,
        });
        this.syncChatPosition();
      }

      observeChatClient() {
        const chatClient = document.querySelector(SELECTORS.chatClient);
        if (!chatClient || chatClient === this.chatClient) return;

        this.chatClient = chatClient;
        this.chatClient.addEventListener('load', this.armChatMountTimeout, { once: true });
        this.chatClient.addEventListener('error', this.stopChatDiscovery, { once: true });
        if (performance.getEntriesByName(this.chatClient.src).length) {
          this.armChatMountTimeout();
          return;
        }

        this.chatClientLoadTimeout = window.setTimeout(this.stopChatDiscovery, CHAT_CLIENT_LOAD_TIMEOUT_MS);
      }

      armChatMountTimeout() {
        window.clearTimeout(this.chatClientLoadTimeout);
        window.clearTimeout(this.chatMountTimeout);
        // ponytail: client success gets one mount grace period; client error or timeout is the failure contract.
        this.chatMountTimeout = window.setTimeout(this.stopChatDiscovery, CHAT_MOUNT_TIMEOUT_MS);
      }

      stopChatDiscovery() {
        this.chatDocumentObserver?.disconnect();
        this.chatDocumentObserver = null;
        window.clearTimeout(this.chatClientLoadTimeout);
        window.clearTimeout(this.chatMountTimeout);
        this.chatClient?.removeEventListener('load', this.armChatMountTimeout);
        this.chatClient?.removeEventListener('error', this.stopChatDiscovery);
      }

      syncChatPosition() {
        const launcher = document.querySelector(SELECTORS.chatLauncher);
        if (!launcher) return;

        if (this.chatLauncher !== launcher) {
          this.chatLauncher = launcher;
          this.chatLauncherObserver?.disconnect();
          this.chatLauncherObserver = new ResizeObserver(this.syncChatPosition);
          this.chatLauncherObserver.observe(launcher);
        }

        const launcherBox = launcher.getBoundingClientRect();
        const buttonBox = this.button.getBoundingClientRect();
        if (!launcherBox.width || !launcherBox.height || !buttonBox.height) return;

        // ponytail: the widget artwork is bottom-aligned; its narrower side is the visible round launcher.
        const launcherSize = Math.min(launcherBox.width, launcherBox.height);
        const launcherBottom = window.innerHeight - launcherBox.bottom;
        this.style.setProperty('--product-floating-bar-inline-end', `${window.innerWidth - launcherBox.left}px`);
        this.style.setProperty(
          '--product-floating-bar-bottom',
          `${launcherBottom + (launcherSize - buttonBox.height) / 2}px`
        );
      }
    }
  );
}
